import React, { useState, useEffect } from "react";
import { View as RNView, Pressable as RNPressable } from "react-native-web";
import CustomText from "../../components/atoms/CustomText";
import {
  colors,
  spacing,
  borderRadius,
  shadows,
} from "../../utils/styling";
import { useCurrentProfile } from "../../lib/profiles";
import { useLogout } from "../../lib/auth";
import PersonalityTab from "../advisor/client/tabs/PersonalityTab";
import { useResponsive } from "../../hooks/useResponsive";
import { PDFViewer } from "@react-pdf/renderer";
import PersonalityTestPDF from "../../components/common/PersonalityTestPDF";
import { useProfileQuizzes } from "../../lib/quizzes";
import {
  resolveStoredAnswers,
  toRenderableIndices,
} from "../../utils/quizinfo/legacyAnswers";
import {
} from "../../utils/category_mappings";
import { resolveWorkbookExercises } from "../../utils/quizinfo/personalityContent";
import { useSubscriptionsByProfile } from "../../lib/subscriptions";
import { createCustomerPortal } from "../../lib/stripe";
import { downloadPDF } from "../../lib/pdf-utils";
import PersonalitySidePanel from "../../components/personality/PersonalitySidePanel";

// Type-safe components
const View = RNView as unknown as React.FC<React.ComponentProps<typeof RNView>>;
const Pressable = RNPressable as unknown as React.FC<
  React.ComponentProps<typeof RNPressable>
>;

// The client's latest quiz, shaped for PersonalityTestPDF. Shared by the
// (hidden) PDF tab and the "Download my results" button so both render the
// same document.
function buildResultsPdfProps(quizzes: any[] | undefined, userName: string) {
  if (!quizzes || quizzes.length === 0) return null;

  // Use the most recent quiz
  const sortedQuizzes = [...quizzes].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const activeQuiz = sortedQuizzes[0];
  const quizAnswers = Array.isArray(activeQuiz.answers?.quiz)
    ? activeQuiz.answers.quiz
    : [];
  const mainResults = activeQuiz?.results?.mainQuiz || {};
  const specificResults = activeQuiz?.results?.specificQuiz || {};

  // Get user info from quiz data
  const userInfo: Array<{
    question: string;
    answer: string;
    question_id: number;
  }> = Array.isArray(activeQuiz.info) ? activeQuiz.info : [];

  // Convert quizAnswers to indices for the PDF.
  // Unmatched answers resolve to -1 (skipped downstream) rather than 0, so a legacy
  // 17-answer record cannot render as 17 confident wrong answers. ClickUp 86bb0fwu5.
  const resolvedAnswers = resolveStoredAnswers(quizAnswers);
  const answersAsIndices = toRenderableIndices(resolvedAnswers);

  function getDominantCategory(results: Record<string, number>): string {
    if (!results || Object.entries(results).length === 0) {
      return "Freedom Finder";
    }
    return Object.entries(results).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
  }

  const dominantCategory = getDominantCategory(mainResults);
  const specificDominantCategory = getDominantCategory(specificResults);

  return {
    answers: answersAsIndices,
    categoryScores: mainResults,
    dominantCategory,
    personalInfo: userInfo.reduce((acc, curr) => {
      acc[curr.question] = curr.answer;
      return acc;
    }, {} as Record<string, string>),
    exercises: resolveWorkbookExercises(specificDominantCategory),
    userName,
  };
}

// PDF Tab Component
function PDFTab({
  profileId,
  userName,
}: {
  profileId: string | undefined;
  userName: string;
}) {
  // By the client's own login: the backend only lets a client list quizzes by
  // profile, never by Salesforce account (2026-09-24).
  const { data: quizzes, isLoading } = useProfileQuizzes(profileId);

  if (!profileId) return null;
  if (isLoading) return <CustomText>Loading...</CustomText>;
  const pdfProps = buildResultsPdfProps(quizzes, userName);
  if (!pdfProps) {
    return <CustomText>No quiz data available.</CustomText>;
  }

  return (
    <View style={{ width: "100%", height: "80vh" }}>
      <PDFViewer style={{ width: "100%", height: "100%" }}>
        <PersonalityTestPDF {...pdfProps} />
      </PDFViewer>
    </View>
  );
}

// The dashboard's "Put it into action" tiles: the same seven actions as the right-hand
// panel on every personality page (McKayla 2026-10-12), plus earlier results.
// "Download My Personality Overview" keeps the 10/7 promise (Nina + McKayla): people
// download their results right where the app shows them, not only from the email.
function DashboardActions({
  profileId,
  userName,
  onExercises,
}: {
  profileId: string | undefined;
  userName: string;
  onExercises: () => void;
}) {
  const { data: quizzes } = useProfileQuizzes(profileId);
  const { isMobile } = useResponsive();

  const pdfProps = buildResultsPdfProps(quizzes, userName);
  if (!pdfProps) return null;

  // Every quiz but the newest, newest first. Retakes keep earlier results (McKayla
  // 2026-10-12: "I'd also like their previous results to remain accessible").
  const earlier = [...(quizzes || [])]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(1)
    .filter((q) => q?.results?.mainQuiz && Object.keys(q.results.mainQuiz).length > 0);

  return (
    <View
      style={{
        width: "100%",
        maxWidth: 1400,
        // The mobile header is fixed and ~120px tall while the page only pads
        // 56px for it, so clear the rest or the tiles sit under the header.
        marginTop: isMobile ? 72 : 0,
        marginBottom: spacing.xl,
        paddingHorizontal: isMobile ? spacing.md : 0,
      }}
    >
      <CustomText
        style={{
          fontSize: isMobile ? 22 : 28,
          fontWeight: "700",
          color: colors.dark,
          fontFamily: "Nunito",
          marginBottom: spacing.md,
        }}
      >
        Put it into action
      </CustomText>
      <PersonalitySidePanel
        personality={pdfProps.dominantCategory}
        layout="grid"
        onDownload={() => downloadPDF(pdfProps)}
        onExercises={onExercises}
      />
      {earlier.length > 0 && (
        <View style={{ marginTop: spacing.lg }}>
          <CustomText
            style={{ fontSize: 18, fontWeight: "700", color: colors.dark, fontFamily: "Nunito", marginBottom: spacing.sm }}
          >
            Your earlier results
          </CustomText>
          {earlier.map((q) => (
            <EarlierResultRow key={q.id} quiz={q} userName={userName} />
          ))}
        </View>
      )}
    </View>
  );
}

function EarlierResultRow({ quiz, userName }: { quiz: any; userName: string }) {
  const [busy, setBusy] = useState(false);
  const props = buildResultsPdfProps([quiz], userName);
  if (!props) return null;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.md,
        backgroundColor: colors.white,
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 12,
        padding: spacing.md,
        marginBottom: spacing.sm,
      }}
    >
      <CustomText style={{ fontSize: 15, color: colors.gray[700], fontFamily: "Nunito" }}>
        {new Date(quiz.created_at).toLocaleDateString()}: <b>{props.dominantCategory}</b>
      </CustomText>
      <Pressable
        onPress={async () => {
          setBusy(true);
          try {
            await downloadPDF(props);
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        style={{ cursor: busy ? "wait" : "pointer" }}
      >
        <CustomText style={{ fontSize: 14, fontWeight: "700", color: colors.primary, fontFamily: "Nunito" }}>
          {busy ? "Preparing..." : "Download PDF"}
        </CustomText>
      </Pressable>
    </View>
  );
}

// Subscription Management Tab Component
function SubscriptionTab({
  profileId,
  userName,
}: {
  profileId: string;
  userName: string;
}) {
  // Real status from the app's subscription records (kept in sync by the Stripe
  // webhook), and a button to Stripe's own billing page where the client can cancel,
  // update their card, or download receipts. Before 2026-09-24 this tab showed a
  // hard-coded "No active subscription." and had no way to cancel.
  const { isMobile } = useResponsive();
  const { data: subscriptions, isLoading: subscriptionsLoading } =
    useSubscriptionsByProfile(profileId);
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = (subscriptions || []).find((s) =>
    ["active", "trialing", "past_due"].includes(s.status)
  );

  const statusText = (s: any) => {
    if (s.cancel_at_period_end) return "Cancelled — active until the end of this billing period";
    if (s.status === "past_due") return "Payment past due";
    if (s.status === "trialing") return "Trial";
    return "Active";
  };

  const handleManage = async () => {
    if (!current?.customer_id) return;
    setIsOpening(true);
    setError(null);
    try {
      const result = await createCustomerPortal(
        current.customer_id,
        `${window.location.origin}/client?refresh=true`
      );
      if (result.status !== "success" || !result.portal_url) {
        throw new Error("Could not open billing page");
      }
      window.location.href = result.portal_url;
    } catch (err: any) {
      setError("We couldn't open your billing page. Please try again.");
      setIsOpening(false);
    }
  };

  return (
    <View style={{ padding: isMobile ? spacing.md : spacing.xl, margin: "0 auto" }}>
      <CustomText
        style={{
          fontSize: 28,
          fontWeight: "700",
          color: colors.dark,
          marginBottom: spacing.lg,
          textAlign: "center",
          fontFamily: "Nunito",
          marginTop: isMobile ? spacing.xxl * 1.25 : undefined,
        }}
      >
        Subscription Management
      </CustomText>

      {subscriptionsLoading ? (
        <CustomText style={{ textAlign: "center", fontFamily: "Nunito", color: colors.gray[600] }}>
          Loading subscription information...
        </CustomText>
      ) : !current ? (
        <CustomText
          style={{
            fontSize: 22,
            fontWeight: "700",
            color: colors.gray[600],
            textAlign: "center",
            fontFamily: "Nunito",
          }}
        >
          No active subscription.
        </CustomText>
      ) : (
        <View style={{ alignItems: "center" }}>
          <CustomText style={{ fontSize: 20, fontWeight: "600", fontFamily: "Nunito", marginBottom: spacing.sm }}>
            {statusText(current)}
          </CustomText>
          {current.current_period_end ? (
            <CustomText style={{ fontSize: 14, color: colors.gray[600], fontFamily: "Nunito", marginBottom: spacing.lg }}>
              {current.cancel_at_period_end ? "Ends" : "Renews"} on{" "}
              {new Date(current.current_period_end).toLocaleDateString()}
            </CustomText>
          ) : null}
          <Pressable
            onPress={handleManage}
            disabled={isOpening}
            style={{
              backgroundColor: colors.primary,
              paddingVertical: spacing.md,
              paddingHorizontal: spacing.xl,
              borderRadius: 8,
              opacity: isOpening ? 0.6 : 1,
            }}
          >
            <CustomText style={{ color: "white", fontSize: 16, fontWeight: "600", fontFamily: "Nunito" }}>
              {isOpening ? "Opening..." : "Manage or cancel subscription"}
            </CustomText>
          </Pressable>
          <CustomText style={{ fontSize: 13, color: colors.gray[500], fontFamily: "Nunito", marginTop: spacing.sm, textAlign: "center" }}>
            Opens a secure billing page where you can cancel, update your card, or download receipts.
          </CustomText>
        </View>
      )}

      {error && (
        <CustomText style={{ color: colors.danger, fontSize: 14, textAlign: "center", marginTop: spacing.md }}>
          {error}
        </CustomText>
      )}
    </View>
  );
}

export default function ClientPortal() {
  const { mutate: logout } = useLogout();
  const { isMobile } = useResponsive();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "results" | "exercises" | "pdf" | "subscription"
  >(
    // "Interactive Exercises" on a personality page links here as ?tab=exercises.
    new URLSearchParams(window.location.search).get("tab") === "exercises"
      ? "exercises"
      : "results"
  );
  const [hasActiveSubscription, setHasActiveSubscription] = useState<
    boolean | null
  >(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // Get current user's profile which contains their Salesforce ID
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useCurrentProfile();
  
  // Get user's subscriptions
  const { data: subscriptions, isLoading: subscriptionsLoading, refetch: refetchSubscriptions } =
    useSubscriptionsByProfile(profile?.id || "");

  // Periodic subscription status check (every 30 seconds)
  useEffect(() => {
    if (!profile?.id) return;
    
    const interval = setInterval(() => {
      refetchSubscriptions();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [profile?.id, refetchSubscriptions]);

  console.log("👤 CLIENT PORTAL: Profile state:", {
    hasProfile: !!profile,
    isProfileLoading,
    profileError,
    sfAccount: profile?.sf_account,
  });

  // Check subscription access
  useEffect(() => {
    if (!profile?.id || subscriptionsLoading) return;

    try {
      setIsCheckingAccess(true);
      
      if (subscriptions && subscriptions.length > 0) {
        // Check for active subscription OR cancelled subscription within billing period
        const validSubscription = subscriptions.find((sub) => {
          // Active subscription
          if (sub.status === "active") {
            return true;
          }
          
          // Cancelled subscription but still within billing period
          if (sub.status === "active" && sub.cancel_at_period_end && sub.current_period_end) {
            const periodEnd = new Date(sub.current_period_end);
            const now = new Date();
            return periodEnd > now; // Still within billing period
          }
          
          return false;
        });
        
        setHasActiveSubscription(!!validSubscription);
      } else {
        setHasActiveSubscription(false);
      }
    } catch (error) {
      console.error("Error checking subscription access:", error);
      setHasActiveSubscription(false);
    } finally {
      setIsCheckingAccess(false);
    }
  }, [profile?.id, subscriptions, subscriptionsLoading]);

  // Check for refresh parameter from Stripe portal return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldRefresh = urlParams.get('refresh');
    
    if (shouldRefresh === 'true') {
      // Remove the refresh parameter from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('refresh');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Force refetch to get latest subscription status
      refetchSubscriptions();
      refetchProfile();
    }
  }, [refetchSubscriptions, refetchProfile]);

  // TEMPORARILY DISABLED FOR FREE MONTH - Redirect if no active subscription
  // useEffect(() => {
  //   if (hasActiveSubscription === false && !isCheckingAccess) {
  //     // Redirect to a page explaining they need an active subscription
  //     window.location.href = "/subscription-required";
  //   }
  // }, [hasActiveSubscription, isCheckingAccess]);
  
  // Loading states - only show loading if we're actually loading and don't have an error
  if ((isProfileLoading || isCheckingAccess || subscriptionsLoading) && !profileError) {
    console.log("⌛ CLIENT PORTAL: Showing loading state");
    return (
      <View style={styles.container}>
        <CustomText style={styles.loadingText}>
          Loading your information...
        </CustomText>
      </View>
    );
  }

  // Error states - show error immediately if profile fetch failed
  if (profileError) {
    console.log("❌ CLIENT PORTAL: Showing error state:", { profileError });
    
    // Check if it's an authentication error
    const isAuthError = (profileError as any)?.response?.status === 401;
    const isProfileNotFound = (profileError as any)?.response?.status === 404;
    
    return (
      <View style={styles.container}>
        <CustomText style={styles.errorText}>
          {isAuthError 
            ? "Your session has expired. Please log in again."
            : isProfileNotFound
            ? "Profile information not found. Please contact support."
            : "Error loading your information. Please try again."
          }
        </CustomText>
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.md,
          marginTop: spacing.lg,
          alignItems: 'center',
        }}>
          {!isAuthError && (
            <Pressable 
              style={{
                backgroundColor: colors.secondary,
                paddingVertical: spacing.md,
                paddingHorizontal: spacing.lg,
                borderRadius: borderRadius.md,
              }}
              onPress={() => refetchProfile()}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                Retry
              </CustomText>
            </Pressable>
          )}
          <Pressable 
            style={{
              backgroundColor: colors.primary,
              paddingVertical: spacing.md,
              paddingHorizontal: spacing.lg,
              borderRadius: borderRadius.md,
            }}
            onPress={() => window.location.href = '/login'}
          >
            <CustomText style={{
              color: colors.white,
              fontSize: 16,
              fontWeight: '600',
              fontFamily: 'Nunito',
            }}>
              {isAuthError ? 'Log In Again' : 'Back to Login'}
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  }

  // Not found state
  if (!profile) {
    console.log("❌ CLIENT PORTAL: No profile found");
    return (
      <View style={styles.container}>
        <CustomText style={styles.errorText}>
          Profile information not found
        </CustomText>
        <Pressable 
          style={{
            backgroundColor: colors.primary,
            paddingVertical: spacing.md,
            paddingHorizontal: spacing.lg,
            borderRadius: borderRadius.md,
            marginTop: spacing.lg,
            alignSelf: 'center',
          }}
          onPress={() => window.location.href = '/login'}
        >
          <CustomText style={{
            color: colors.white,
            fontSize: 16,
            fontWeight: '600',
            fontFamily: 'Nunito',
          }}>
            Back to Login
          </CustomText>
        </Pressable>
      </View>
    );
  }

  // Access denied state
  // if (hasActiveSubscription === false) {
  //   return (
  //     <View style={styles.container}>
  //       <CustomText style={styles.errorText}>
  //         Access denied. An active subscription is required.
  //       </CustomText>
  //     </View>
  //   );
  // }

  // Get user's full name
  const fullName =
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Client";

  // Check if user should see PDF tab (temporarily hidden)
  const shouldShowPDFTab = false;

  // Header style: fixed on mobile, static on desktop
  const headerStyle = {
    ...styles.header,
    ...(isMobile
      ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          width: "100vw",
        }
      : {}),
  };

  // Main content style: add top padding on mobile for fixed header
  const mainContentStyle = {
    ...styles.content,
    ...(isMobile ? { paddingTop: 56 } : {}),
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={headerStyle}>
        <View style={styles.content}>
          <View
            style={{
              ...styles.headerContent,
              position: "relative",
              minHeight: 56,
            }}
          >
            {/* Logo and name left */}
            <View style={styles.headerLeft}>
              <img
                src={"/transparent-logo.png"}
                alt="Money Personality Logo"
                style={{
                  height: 48,
                  width: "auto",
                  objectFit: "contain",
                  marginRight: spacing.lg,
                }}
              />
              {!isMobile && (
                <CustomText style={styles.clientName}>{fullName}</CustomText>
              )}
            </View>
            {/* Name between the logo and the menu on mobile only. It used to be
                absolutely centered across the whole bar, so a long name ran
                over the logo (2026-10-07 mobile pass). */}
            {isMobile && (
              <CustomText
                numberOfLines={1}
                style={{
                  ...styles.clientName,
                  flex: 1,
                  minWidth: 0,
                  margin: 0,
                  marginHorizontal: spacing.sm,
                  fontSize: 18,
                  textAlign: "center",
                }}
              >
                {fullName}
              </CustomText>
            )}
            {/* Tab buttons and logout (desktop) or hamburger (mobile) */}
            <View style={styles.headerRight}>
              {!isMobile && (
                <>
                  {/* Tab buttons and logout */}
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Pressable
                      style={{
                        paddingVertical: 16,
                        paddingHorizontal: spacing.md,
                        position: "relative",
                        marginRight: spacing.md,
                      }}
                      onPress={() => setActiveTab("results")}
                    >
                      <CustomText
                        style={{
                          color:
                            activeTab === "results"
                              ? colors.primary
                              : colors.gray[600],
                          fontWeight: "600",
                          fontSize: 14,
                          fontFamily: "Nunito",
                        }}
                      >
                        Results
                      </CustomText>
                      {activeTab === "results" && (
                        <View
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            backgroundColor: colors.primary,
                          }}
                        />
                      )}
                    </Pressable>

                    <Pressable
                      style={{
                        paddingVertical: 16,
                        paddingHorizontal: spacing.md,
                        position: "relative",
                        marginRight: spacing.md,
                      }}
                      onPress={() => setActiveTab("exercises")}
                    >
                      <CustomText
                        style={{
                          color:
                            activeTab === "exercises"
                              ? colors.primary
                              : colors.gray[600],
                          fontWeight: "600",
                          fontSize: 14,
                          fontFamily: "Nunito",
                        }}
                      >
                        Exercises
                      </CustomText>
                      {activeTab === "exercises" && (
                        <View
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            backgroundColor: colors.primary,
                          }}
                        />
                      )}
                    </Pressable>

                    <Pressable
                      style={{
                        paddingVertical: 16,
                        paddingHorizontal: spacing.md,
                        position: "relative",
                        marginRight: spacing.md,
                      }}
                      onPress={() => setActiveTab("subscription")}
                    >
                      <CustomText
                        style={{
                          color:
                            activeTab === "subscription"
                              ? colors.primary
                              : colors.gray[600],
                          fontWeight: "600",
                          fontSize: 14,
                          fontFamily: "Nunito",
                        }}
                      >
                        Subscription
                      </CustomText>
                      {activeTab === "subscription" && (
                        <View
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            backgroundColor: colors.primary,
                          }}
                        />
                      )}
                    </Pressable>

                    {shouldShowPDFTab && (
                      <Pressable
                        style={{
                          paddingVertical: 16,
                          paddingHorizontal: spacing.md,
                          position: "relative",
                          marginRight: spacing.lg,
                        }}
                        onPress={() => setActiveTab("pdf")}
                      >
                        <CustomText
                          style={{
                            color:
                              activeTab === "pdf"
                                ? colors.primary
                                : colors.gray[600],
                            fontWeight: "600",
                            fontSize: 14,
                            fontFamily: "Nunito",
                          }}
                        >
                          PDF
                        </CustomText>
                        {activeTab === "pdf" && (
                          <View
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 2,
                              backgroundColor: colors.primary,
                            }}
                          />
                        )}
                      </Pressable>
                    )}

                    {/* Logout button */}
                    <Pressable
                      onPress={() => logout()}
                      style={({ hovered }: { hovered: boolean }) => ({
                        paddingVertical: 16,
                        paddingHorizontal: spacing.md,
                        backgroundColor: hovered
                          ? colors.gray[100]
                          : "transparent",
                        borderRadius: 6,
                        cursor: "pointer",
                      })}
                    >
                      <CustomText
                        style={{
                          color: colors.gray[600],
                          fontSize: 14,
                          fontFamily: "Nunito",
                          fontWeight: "400",
                        }}
                      >
                        Logout
                      </CustomText>
                    </Pressable>
                  </View>
                </>
              )}
              {isMobile && (
                <Pressable
                  onPress={() => setMobileMenuOpen(!mobileMenuOpen)}
                  style={{ padding: spacing.sm }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 2,
                      backgroundColor: colors.dark,
                      marginBottom: 6,
                    }}
                  />
                  <View
                    style={{
                      width: 24,
                      height: 2,
                      backgroundColor: colors.dark,
                      marginBottom: 6,
                    }}
                  />
                  <View
                    style={{
                      width: 24,
                      height: 2,
                      backgroundColor: colors.dark,
                    }}
                  />
                </Pressable>
              )}
            </View>
          </View>

          {/* Mobile Menu */}
          {isMobile && mobileMenuOpen && (
            <View
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: colors.white,
                borderBottomLeftRadius: borderRadius.lg,
                borderBottomRightRadius: borderRadius.lg,
                boxShadow: shadows.lg,
                padding: spacing.lg,
                zIndex: 1000,
              }}
            >
              {[
                {
                  label: "Results",
                  onPress: () => {
                    setActiveTab("results");
                    setMobileMenuOpen(false);
                  },
                  style: {
                    color:
                      activeTab === "results" ? colors.dark : colors.gray[600],
                    fontSize: 18,
                    fontWeight: activeTab === "results" ? "700" : "500",
                  },
                },
                {
                  label: "Exercises",
                  onPress: () => {
                    setActiveTab("exercises");
                    setMobileMenuOpen(false);
                  },
                  style: {
                    color:
                      activeTab === "exercises"
                        ? colors.dark
                        : colors.gray[600],
                    fontSize: 18,
                    fontWeight: activeTab === "exercises" ? "700" : "500",
                  },
                },
                {
                  label: "Subscription",
                  onPress: () => {
                    setActiveTab("subscription");
                    setMobileMenuOpen(false);
                  },
                  style: {
                    color:
                      activeTab === "subscription"
                        ? colors.dark
                        : colors.gray[600],
                    fontSize: 18,
                    fontWeight: activeTab === "subscription" ? "700" : "500",
                  },
                },
                ...(shouldShowPDFTab
                  ? [
                      {
                        label: "PDF",
                        onPress: () => {
                          setActiveTab("pdf");
                          setMobileMenuOpen(false);
                        },
                        style: {
                          color:
                            activeTab === "pdf"
                              ? colors.dark
                              : colors.gray[600],
                          fontSize: 18,
                          fontWeight: activeTab === "pdf" ? "700" : "500",
                        },
                      },
                    ]
                  : []),
                {
                  label: "Logout",
                  onPress: () => {
                    setMobileMenuOpen(false);
                    logout();
                  },
                  style: {
                    color: colors.gray[600],
                    fontSize: 18,
                    fontWeight: "400",
                  },
                },
              ].map((item, idx) => (
                <Pressable
                  key={item.label}
                  style={({
                    hovered,
                    pressed,
                  }: {
                    hovered: boolean;
                    pressed: boolean;
                  }) => ({
                    padding: spacing.md,
                    backgroundColor:
                      hovered || pressed ? colors.gray[200] : "transparent",
                    borderRadius: 8,
                    transition: "background 0.15s",
                    cursor: "pointer",
                  })}
                  onPress={item.onPress}
                >
                  <CustomText style={item.style}>{item.label}</CustomText>
                </Pressable>
              ))}
            </View>
          )}
        </View>
      </View>

      {/* Main Content */}
      <View style={mainContentStyle}>
        {activeTab === "pdf" ? (
          <PDFTab
            profileId={profile?.id}
            userName={fullName}
          />
        ) : activeTab === "subscription" ? (
          <SubscriptionTab profileId={profile.id} userName={fullName} />
        ) : (
          <>
          {activeTab === "results" && (
            <DashboardActions
              profileId={profile?.id}
              userName={fullName}
              onExercises={() => setActiveTab("exercises")}
            />
          )}
          <PersonalityTab
            sfAccount={profile?.sf_account || undefined}
            activeTab={activeTab}
            userName={fullName}
            setActiveTab={setActiveTab}
          />
          </>
        )}
      </View>
      {/* Mobile Footer */}
      {isMobile && (
        <View
          style={{
            width: "100%",
            padding: spacing.lg,
            backgroundColor: "#262626",
            marginTop: "auto",
          }}
        >
          <View
            style={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: 1200,
              marginLeft: "auto",
              marginRight: "auto",
              width: "100%",
            }}
          >
            {/* Logo and tagline */}
            <View style={{ alignItems: "center", marginBottom: spacing.md }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: spacing.md,
                }}
              >
                <img
                  src="/transparent-logo.png"
                  alt="Money Personality Logo"
                  style={{ width: 55, height: 55, marginRight: spacing.sm }}
                />
              </View>
              <CustomText
                style={{
                  color: colors.white,
                  fontSize: 18,
                  marginBottom: spacing.md,
                  textAlign: "center",
                }}
              >
                Behavioral finance at its finest
              </CustomText>
            </View>
            {/* Privacy/Terms */}
            <View
              style={{
                flexDirection: "row",
                gap: spacing.md,
                alignItems: "center",
                marginBottom: spacing.sm,
                marginTop: 0,
              }}
            >
              <CustomText style={{ color: colors.gray[400], fontSize: 14 }}>
                Privacy
              </CustomText>
              <CustomText style={{ color: colors.gray[400], fontSize: 14 }}>
                Terms
              </CustomText>
            </View>
            {/* Divider */}
            <View
              style={{
                width: "100%",
                height: 1,
                backgroundColor: "rgba(255,255,255,0.1)",
                marginBottom: spacing.md,
              }}
            />
            {/* Copyright */}
            <View
              style={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                gap: spacing.md,
              }}
            >
              <CustomText
                style={{
                  color: colors.gray[400],
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                Headquartered in Los Angeles, CA | © 2025 Money Personality, Inc. All
                rights reserved.
              </CustomText>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.gray[100],
    minHeight: "100vh",
  },
  header: {
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.gray[200]}`,
    width: "100%",
  },
  content: {
    padding: spacing.xl,
    width: "100%",
    // maxWidth: 1200, // REMOVE maxWidth for full width
    marginHorizontal: "auto",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: undefined, // Remove gap so left and right are spaced by space-between
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.lg, // Add gap between logo and name
  },
  clientName: {
    fontSize: 24,
    fontWeight: "600",
    color: colors.dark,
  },
  headerRight: {
    flexDirection: "row",
    gap: spacing.md,
  },
  loadingText: {
    fontSize: 16,
    color: colors.gray[600],
    textAlign: "center",
    marginTop: spacing.xl,
  },
  errorText: {
    fontSize: 16,
    color: colors.danger,
    textAlign: "center",
    marginTop: spacing.xl,
  },
} as const;
